import React, { useEffect, useState } from 'react';
import { useRouteError } from 'react-router-dom';
import { getErrorReporter } from 'shared/services/error-tracking';
import { UserReadError } from 'shared/types/user-read-error';
import {
  isChunkLoadError,
  reloadAfterChunkLoadFailure,
} from 'shared/utils/reload-after-chunk-load-failure';

import { version } from '../../../package.json';
import { GenericError, GenericErrorVariants } from './generic-error';

const notFoundCodes = ['permission-denied'];

export const ErrorPage: React.FC = () => {
  const error = useRouteError() as any;
  const [errors, setErrors] = useState<string[]>([]);
  // A failed read of the user's own document is never a missing page, whatever code it carries.
  const isUserReadError = error instanceof UserReadError;
  // Decided once per mount: the reload is already under way, so the error page and its report are skipped.
  const [isReloading] = useState(() => reloadAfterChunkLoadFailure(error));

  useEffect(() => {
    if (isReloading) {
      return;
    }
    const eventId = getErrorReporter().captureException(error, {
      tags: {
        source: 'route-error-boundary',
        // reached only when the reload was skipped or already spent on this failure
        ...(isChunkLoadError(error) && { chunkLoadFailure: 'true' }),
      },
      extra: { code: error?.code },
    });
    if (!eventId) {
      return;
    }
    setErrors((oldIds) => [
      ...oldIds,
      [version, eventId, error?.code].filter(Boolean).join('-'),
    ]);
  }, [error, isReloading]);

  if (isReloading) {
    return null;
  }

  if (isUserReadError) {
    return (
      <GenericError
        errors={errors}
        variant={GenericErrorVariants.AccountUnavailable}
      />
    );
  }

  if (!error || notFoundCodes.includes(error.code)) {
    return (
      <GenericError errors={errors} variant={GenericErrorVariants.NotFound} />
    );
  }

  return <GenericError errors={errors} />;
};
